import { supabase } from './supabase';

export interface ForecastClient {
  id: string;
  brand_id: string;
  company_name: string;
  contact_name?: string;
  contact_email?: string;
  status: 'active' | 'inactive' | 'abandoned';
  activation_date: string;
  deactivation_date?: string;
  payment_reliability_score: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface RecurringService {
  id: string;
  client_id: string;
  service_name: string;
  monthly_rate: number;
  billing_day: number;
  contract_start_date: string;
  contract_end_date?: string;
  final_payment_received: boolean;
  prorated_amount?: number;
  status: 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  client_id: string;
  project_name: string;
  total_value: number;
  estimated_duration_months?: number;
  start_date: string;
  estimated_completion_date?: string;
  actual_completion_date?: string;
  status: 'active' | 'completed' | 'abandoned';
  total_paid: number;
  abandonment_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectMilestone {
  id: string;
  project_id: string;
  milestone_name: string;
  percentage_of_total: number;
  amount: number;
  estimated_payment_date: string;
  actual_payment_date?: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  payment_received: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ProratedPayment {
  id: string;
  service_id: string;
  payment_date: string;
  full_monthly_rate: number;
  days_billed: number;
  days_in_month: number;
  prorated_amount: number;
  payment_received: boolean;
  is_final_payment: boolean;
  created_at: string;
}

export interface AbandonedRevenue {
  id: string;
  client_id: string;
  project_id?: string;
  service_id?: string;
  expected_amount: number;
  actual_amount: number;
  revenue_loss: number;
  abandonment_date: string;
  abandonment_type: 'project_abandoned' | 'service_cancelled' | 'payment_default';
  notes?: string;
  created_at: string;
}

export interface ExpenseCategory {
  id: string;
  brand_id: string;
  name: string;
  description?: string;
  created_at: string;
}

export interface Expense {
  id: string;
  brand_id: string;
  category_id?: string;
  expense_name: string;
  amount: number;
  expense_type: 'one_time' | 'recurring';
  expense_date?: string;
  recurrence_day?: number;
  recurrence_start_date?: string;
  recurrence_end_date?: string;
  status: 'active' | 'completed' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'overdue';
  notes?: string;
  is_shared: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExpenseAllocation {
  id: string;
  expense_id: string;
  brand_id: string;
  allocation_percentage: number;
  allocated_amount: number;
  created_at: string;
}

export interface CSVImport {
  id: string;
  brand_id: string;
  import_type: 'clients' | 'expenses';
  file_name: string;
  records_total: number;
  records_imported: number;
  records_failed: number;
  error_log?: any;
  imported_by: string;
  created_at: string;
}

export interface ForecastScenario {
  id: string;
  brand_id: string;
  scenario_name: string;
  description?: string;
  churn_rate_monthly: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface MonthlyForecast {
  month: string;
  recurring_revenue: number;
  prorated_revenue: number;
  project_revenue: number;
  total_revenue: number;
  confirmed_revenue: number;
  at_risk_revenue: number;
  one_time_expenses?: number;
  recurring_expenses?: number;
  total_expenses?: number;
  net_profit?: number;
  churn_adjusted_recurring_revenue?: number;
  churn_adjusted_total_revenue?: number;
  revenue_lost_to_churn?: number;
}

export const forecastService = {
  async getClients(brandId: string): Promise<ForecastClient[]> {
    const { data, error } = await supabase
      .from('forecast_clients')
      .select('*')
      .eq('brand_id', brandId)
      .order('company_name');

    if (error) throw error;
    return data || [];
  },

  async getClient(clientId: string): Promise<ForecastClient | null> {
    const { data, error } = await supabase
      .from('forecast_clients')
      .select('*')
      .eq('id', clientId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async createClient(client: Omit<ForecastClient, 'id' | 'created_at' | 'updated_at' | 'payment_reliability_score'>): Promise<ForecastClient> {
    const { data, error } = await supabase
      .from('forecast_clients')
      .insert(client)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateClient(clientId: string, updates: Partial<ForecastClient>): Promise<ForecastClient> {
    const { data, error } = await supabase
      .from('forecast_clients')
      .update(updates)
      .eq('id', clientId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteClient(clientId: string): Promise<void> {
    const { error } = await supabase
      .from('forecast_clients')
      .delete()
      .eq('id', clientId);

    if (error) throw error;
  },

  async getRecurringServices(clientId: string): Promise<RecurringService[]> {
    const { data, error } = await supabase
      .from('forecast_recurring_services')
      .select('*')
      .eq('client_id', clientId)
      .order('service_name');

    if (error) throw error;
    return data || [];
  },

  async createRecurringService(service: Omit<RecurringService, 'id' | 'created_at' | 'updated_at'>): Promise<RecurringService> {
    const { data, error } = await supabase
      .from('forecast_recurring_services')
      .insert(service)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateRecurringService(serviceId: string, updates: Partial<RecurringService>): Promise<RecurringService> {
    const { data, error } = await supabase
      .from('forecast_recurring_services')
      .update(updates)
      .eq('id', serviceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteRecurringService(serviceId: string): Promise<void> {
    const { error } = await supabase
      .from('forecast_recurring_services')
      .delete()
      .eq('id', serviceId);

    if (error) throw error;
  },

  async getProjects(clientId: string): Promise<Project[]> {
    const { data, error } = await supabase
      .from('forecast_projects')
      .select('*')
      .eq('client_id', clientId)
      .order('project_name');

    if (error) throw error;
    return data || [];
  },

  async createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at' | 'total_paid'>): Promise<Project> {
    const { data, error } = await supabase
      .from('forecast_projects')
      .insert(project)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateProject(projectId: string, updates: Partial<Project>): Promise<Project> {
    const { data, error } = await supabase
      .from('forecast_projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteProject(projectId: string): Promise<void> {
    const { error } = await supabase
      .from('forecast_projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
  },

  async getMilestones(projectId: string): Promise<ProjectMilestone[]> {
    const { data, error } = await supabase
      .from('forecast_project_milestones')
      .select('*')
      .eq('project_id', projectId)
      .order('estimated_payment_date');

    if (error) throw error;
    return data || [];
  },

  async createMilestone(milestone: Omit<ProjectMilestone, 'id' | 'created_at' | 'updated_at'>): Promise<ProjectMilestone> {
    const { data, error } = await supabase
      .from('forecast_project_milestones')
      .insert(milestone)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateMilestone(milestoneId: string, updates: Partial<ProjectMilestone>): Promise<ProjectMilestone> {
    const { data, error } = await supabase
      .from('forecast_project_milestones')
      .update(updates)
      .eq('id', milestoneId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteMilestone(milestoneId: string): Promise<void> {
    const { error } = await supabase
      .from('forecast_project_milestones')
      .delete()
      .eq('id', milestoneId);

    if (error) throw error;
  },

  async getProratedPayments(serviceId: string): Promise<ProratedPayment[]> {
    const { data, error } = await supabase
      .from('forecast_prorated_payments')
      .select('*')
      .eq('service_id', serviceId)
      .order('payment_date');

    if (error) throw error;
    return data || [];
  },

  async createProratedPayment(payment: Omit<ProratedPayment, 'id' | 'created_at'>): Promise<ProratedPayment> {
    const { data, error } = await supabase
      .from('forecast_prorated_payments')
      .insert(payment)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateProratedPayment(paymentId: string, updates: Partial<ProratedPayment>): Promise<ProratedPayment> {
    const { data, error } = await supabase
      .from('forecast_prorated_payments')
      .update(updates)
      .eq('id', paymentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getAbandonedRevenue(clientId: string): Promise<AbandonedRevenue[]> {
    const { data, error } = await supabase
      .from('forecast_abandoned_revenue')
      .select('*')
      .eq('client_id', clientId)
      .order('abandonment_date', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createAbandonedRevenue(revenue: Omit<AbandonedRevenue, 'id' | 'created_at'>): Promise<AbandonedRevenue> {
    const { data, error } = await supabase
      .from('forecast_abandoned_revenue')
      .insert(revenue)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  calculateProratedAmount(monthlyRate: number, startDay: number, endDay: number, daysInMonth: number): number {
    const daysBilled = endDay - startDay + 1;
    const dailyRate = monthlyRate / daysInMonth;
    return dailyRate * daysBilled;
  },

  getDaysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
  },

  async generateForecast(brandId: string, monthsAhead: number = 12): Promise<MonthlyForecast[]> {
    const clients = await this.getClients(brandId);
    const forecasts: MonthlyForecast[] = [];

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    for (let i = 0; i < monthsAhead; i++) {
      const forecastDate = new Date(currentYear, currentMonth + i, 1);
      const year = forecastDate.getFullYear();
      const month = forecastDate.getMonth();
      const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

      let recurringRevenue = 0;
      let proratedRevenue = 0;
      let projectRevenue = 0;
      let confirmedRevenue = 0;
      let atRiskRevenue = 0;

      for (const client of clients) {
        const services = await this.getRecurringServices(client.id);

        for (const service of services) {
          if (service.status === 'active') {
            const serviceStart = new Date(service.contract_start_date);
            const serviceEnd = service.contract_end_date ? new Date(service.contract_end_date) : null;

            if (serviceStart <= forecastDate && (!serviceEnd || serviceEnd >= forecastDate)) {
              if (serviceEnd && serviceEnd.getMonth() === month && serviceEnd.getFullYear() === year) {
                const daysInMonth = this.getDaysInMonth(year, month + 1);
                const prorated = this.calculateProratedAmount(
                  service.monthly_rate,
                  1,
                  serviceEnd.getDate(),
                  daysInMonth
                );
                proratedRevenue += prorated;
                if (service.final_payment_received) {
                  confirmedRevenue += prorated;
                } else {
                  atRiskRevenue += prorated;
                }
              } else {
                recurringRevenue += service.monthly_rate;
                confirmedRevenue += service.monthly_rate;
              }
            }
          }
        }

        const projects = await this.getProjects(client.id);

        for (const project of projects) {
          if (project.status === 'active' || project.status === 'completed') {
            const milestones = await this.getMilestones(project.id);

            for (const milestone of milestones) {
              if (milestone.status !== 'cancelled') {
                const milestoneDate = new Date(milestone.estimated_payment_date);
                if (milestoneDate.getMonth() === month && milestoneDate.getFullYear() === year) {
                  projectRevenue += milestone.amount;
                  if (milestone.payment_received) {
                    confirmedRevenue += milestone.amount;
                  } else {
                    atRiskRevenue += milestone.amount;
                  }
                }
              }
            }
          }
        }
      }

      forecasts.push({
        month: monthKey,
        recurring_revenue: recurringRevenue,
        prorated_revenue: proratedRevenue,
        project_revenue: projectRevenue,
        total_revenue: recurringRevenue + proratedRevenue + projectRevenue,
        confirmed_revenue: confirmedRevenue,
        at_risk_revenue: atRiskRevenue,
      });
    }

    return forecasts;
  },

  async abandonProject(
    projectId: string,
    lastCompletedMilestoneId: string | null,
    notes?: string
  ): Promise<void> {
    const project = await supabase
      .from('forecast_projects')
      .select('*, forecast_project_milestones(*)')
      .eq('id', projectId)
      .single();

    if (project.error) throw project.error;

    const milestones = await this.getMilestones(projectId);
    let expectedAmount = 0;
    let actualAmount = 0;

    for (const milestone of milestones) {
      if (milestone.status === 'paid' || milestone.payment_received) {
        actualAmount += milestone.amount;
      }
      if (lastCompletedMilestoneId && milestone.id === lastCompletedMilestoneId) {
        break;
      }
      expectedAmount += milestone.amount;
    }

    await this.updateProject(projectId, {
      status: 'abandoned',
      abandonment_notes: notes,
    });

    for (const milestone of milestones) {
      if (!milestone.payment_received && milestone.status === 'pending') {
        await this.updateMilestone(milestone.id, {
          status: 'cancelled',
        });
      }
    }

    if (expectedAmount > actualAmount) {
      await this.createAbandonedRevenue({
        client_id: project.data.client_id,
        project_id: projectId,
        expected_amount: expectedAmount,
        actual_amount: actualAmount,
        revenue_loss: expectedAmount - actualAmount,
        abandonment_date: new Date().toISOString().split('T')[0],
        abandonment_type: 'project_abandoned',
        notes,
      });
    }
  },

  async cancelService(
    serviceId: string,
    endDate: string,
    finalPaymentReceived: boolean,
    notes?: string
  ): Promise<void> {
    const service = await supabase
      .from('forecast_recurring_services')
      .select('*')
      .eq('id', serviceId)
      .single();

    if (service.error) throw service.error;

    const endDateObj = new Date(endDate);
    const year = endDateObj.getFullYear();
    const month = endDateObj.getMonth() + 1;
    const daysInMonth = this.getDaysInMonth(year, month);

    const proratedAmount = this.calculateProratedAmount(
      service.data.monthly_rate,
      1,
      endDateObj.getDate(),
      daysInMonth
    );

    await this.updateRecurringService(serviceId, {
      contract_end_date: endDate,
      status: 'cancelled',
      final_payment_received: finalPaymentReceived,
      prorated_amount: proratedAmount,
    });

    await this.createProratedPayment({
      service_id: serviceId,
      payment_date: endDate,
      full_monthly_rate: service.data.monthly_rate,
      days_billed: endDateObj.getDate(),
      days_in_month: daysInMonth,
      prorated_amount: proratedAmount,
      payment_received: finalPaymentReceived,
      is_final_payment: true,
    });

    if (!finalPaymentReceived) {
      await this.createAbandonedRevenue({
        client_id: service.data.client_id,
        service_id: serviceId,
        expected_amount: proratedAmount,
        actual_amount: 0,
        revenue_loss: proratedAmount,
        abandonment_date: endDate,
        abandonment_type: 'service_cancelled',
        notes,
      });
    }
  },

  async getExpenseCategories(brandId: string): Promise<ExpenseCategory[]> {
    const { data, error } = await supabase
      .from('forecast_expense_categories')
      .select('*')
      .eq('brand_id', brandId)
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getExpenses(brandId: string): Promise<Expense[]> {
    const { data, error } = await supabase
      .from('forecast_expenses')
      .select('*')
      .eq('brand_id', brandId)
      .order('expense_name');

    if (error) throw error;
    return data || [];
  },

  async createExpense(expense: Omit<Expense, 'id' | 'created_at' | 'updated_at'>): Promise<Expense> {
    const { data, error } = await supabase
      .from('forecast_expenses')
      .insert(expense)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateExpense(expenseId: string, updates: Partial<Expense>): Promise<Expense> {
    const { data, error } = await supabase
      .from('forecast_expenses')
      .update(updates)
      .eq('id', expenseId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteExpense(expenseId: string): Promise<void> {
    const { error } = await supabase
      .from('forecast_expenses')
      .delete()
      .eq('id', expenseId);

    if (error) throw error;
  },

  async createExpenseCategory(category: Omit<ExpenseCategory, 'id' | 'created_at'>): Promise<ExpenseCategory> {
    const { data, error } = await supabase
      .from('forecast_expense_categories')
      .insert(category)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getExpenseAllocations(expenseId: string): Promise<ExpenseAllocation[]> {
    const { data, error } = await supabase
      .from('forecast_expense_allocations')
      .select('*')
      .eq('expense_id', expenseId)
      .order('allocation_percentage', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createExpenseWithAllocations(
    expense: Omit<Expense, 'id' | 'created_at' | 'updated_at'>,
    allocations: { brand_id: string; allocation_percentage: number }[]
  ): Promise<Expense> {
    const { data: expenseData, error: expenseError } = await supabase
      .from('forecast_expenses')
      .insert({
        ...expense,
        is_shared: allocations.length > 1,
      })
      .select()
      .single();

    if (expenseError) throw expenseError;

    const allocationInserts = allocations.map(alloc => ({
      expense_id: expenseData.id,
      brand_id: alloc.brand_id,
      allocation_percentage: alloc.allocation_percentage,
    }));

    const { error: allocError } = await supabase
      .from('forecast_expense_allocations')
      .insert(allocationInserts);

    if (allocError) throw allocError;

    return expenseData;
  },

  async updateExpenseAllocations(
    expenseId: string,
    allocations: { brand_id: string; allocation_percentage: number }[]
  ): Promise<void> {
    await supabase
      .from('forecast_expense_allocations')
      .delete()
      .eq('expense_id', expenseId);

    const allocationInserts = allocations.map(alloc => ({
      expense_id: expenseId,
      brand_id: alloc.brand_id,
      allocation_percentage: alloc.allocation_percentage,
    }));

    const { error } = await supabase
      .from('forecast_expense_allocations')
      .insert(allocationInserts);

    if (error) throw error;

    await supabase
      .from('forecast_expenses')
      .update({ is_shared: allocations.length > 1 })
      .eq('id', expenseId);
  },

  async getExpensesForBrand(brandId: string): Promise<(Expense & { allocated_amount?: number })[]> {
    const { data: allocations, error: allocError } = await supabase
      .from('forecast_expense_allocations')
      .select(`
        *,
        expense:forecast_expenses(*)
      `)
      .eq('brand_id', brandId);

    if (allocError) throw allocError;

    return (allocations || []).map(alloc => ({
      ...alloc.expense,
      allocated_amount: alloc.allocated_amount,
    }));
  },

  async getAllExpensesWithAllocations(): Promise<Expense[]> {
    const { data, error } = await supabase
      .from('forecast_expenses')
      .select('*')
      .order('expense_name');

    if (error) throw error;
    return data || [];
  },

  async importClientsFromCSV(brandId: string, userId: string, csvData: any[]): Promise<CSVImport> {
    let recordsImported = 0;
    let recordsFailed = 0;
    const errors: any[] = [];

    for (let i = 0; i < csvData.length; i++) {
      try {
        const row = csvData[i];
        await this.createClient({
          brand_id: brandId,
          company_name: row.company_name,
          contact_name: row.contact_name || undefined,
          contact_email: row.contact_email || undefined,
          status: row.status || 'active',
          activation_date: row.activation_date || new Date().toISOString().split('T')[0],
          notes: row.notes || undefined,
        });
        recordsImported++;
      } catch (error) {
        recordsFailed++;
        errors.push({ row: i + 1, error: String(error) });
      }
    }

    const { data, error } = await supabase
      .from('forecast_csv_imports')
      .insert({
        brand_id: brandId,
        import_type: 'clients',
        file_name: 'clients_import.csv',
        records_total: csvData.length,
        records_imported: recordsImported,
        records_failed: recordsFailed,
        error_log: errors.length > 0 ? errors : null,
        imported_by: userId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async importExpensesFromCSV(brandId: string, userId: string, csvData: any[]): Promise<CSVImport> {
    let recordsImported = 0;
    let recordsFailed = 0;
    const errors: any[] = [];

    for (let i = 0; i < csvData.length; i++) {
      try {
        const row = csvData[i];
        await this.createExpense({
          brand_id: brandId,
          expense_name: row.expense_name,
          amount: parseFloat(row.amount),
          expense_type: row.expense_type || 'one_time',
          expense_date: row.expense_date || undefined,
          recurrence_day: row.recurrence_day ? parseInt(row.recurrence_day) : undefined,
          recurrence_start_date: row.recurrence_start_date || undefined,
          recurrence_end_date: row.recurrence_end_date || undefined,
          status: row.status || 'active',
          payment_status: row.payment_status || 'pending',
          notes: row.notes || undefined,
          category_id: row.category_id || undefined,
        });
        recordsImported++;
      } catch (error) {
        recordsFailed++;
        errors.push({ row: i + 1, error: String(error) });
      }
    }

    const { data, error } = await supabase
      .from('forecast_csv_imports')
      .insert({
        brand_id: brandId,
        import_type: 'expenses',
        file_name: 'expenses_import.csv',
        records_total: csvData.length,
        records_imported: recordsImported,
        records_failed: recordsFailed,
        error_log: errors.length > 0 ? errors : null,
        imported_by: userId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async generateProfitLossForecast(brandId: string, monthsAhead: number = 12): Promise<MonthlyForecast[]> {
    const [revenueForecast, expenses] = await Promise.all([
      this.generateForecast(brandId, monthsAhead),
      this.getExpensesForBrand(brandId),
    ]);

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    return revenueForecast.map((forecast, index) => {
      const forecastDate = new Date(currentYear, currentMonth + index, 1);
      const year = forecastDate.getFullYear();
      const month = forecastDate.getMonth();

      let oneTimeExpenses = 0;
      let recurringExpenses = 0;

      for (const expense of expenses) {
        if (expense.status === 'cancelled') continue;

        const expenseAmount = expense.allocated_amount || expense.amount;

        if (expense.expense_type === 'one_time' && expense.expense_date) {
          const expenseDate = new Date(expense.expense_date);
          if (expenseDate.getMonth() === month && expenseDate.getFullYear() === year) {
            oneTimeExpenses += expenseAmount;
          }
        } else if (expense.expense_type === 'recurring') {
          const startDate = expense.recurrence_start_date ? new Date(expense.recurrence_start_date) : null;
          const endDate = expense.recurrence_end_date ? new Date(expense.recurrence_end_date) : null;

          if ((!startDate || startDate <= forecastDate) && (!endDate || endDate >= forecastDate)) {
            recurringExpenses += expenseAmount;
          }
        }
      }

      const totalExpenses = oneTimeExpenses + recurringExpenses;
      const netProfit = forecast.total_revenue - totalExpenses;

      return {
        ...forecast,
        one_time_expenses: oneTimeExpenses,
        recurring_expenses: recurringExpenses,
        total_expenses: totalExpenses,
        net_profit: netProfit,
      };
    });
  },

  async generateConsolidatedProfitLoss(brandIds: string[], monthsAhead: number = 12): Promise<MonthlyForecast[]> {
    const forecasts = await Promise.all(
      brandIds.map(brandId => this.generateProfitLossForecast(brandId, monthsAhead))
    );

    const consolidated: MonthlyForecast[] = [];

    for (let i = 0; i < monthsAhead; i++) {
      let monthData: MonthlyForecast = {
        month: forecasts[0][i].month,
        recurring_revenue: 0,
        prorated_revenue: 0,
        project_revenue: 0,
        total_revenue: 0,
        confirmed_revenue: 0,
        at_risk_revenue: 0,
        one_time_expenses: 0,
        recurring_expenses: 0,
        total_expenses: 0,
        net_profit: 0,
      };

      for (const forecast of forecasts) {
        const monthForecast = forecast[i];
        monthData.recurring_revenue += monthForecast.recurring_revenue;
        monthData.prorated_revenue += monthForecast.prorated_revenue;
        monthData.project_revenue += monthForecast.project_revenue;
        monthData.total_revenue += monthForecast.total_revenue;
        monthData.confirmed_revenue += monthForecast.confirmed_revenue;
        monthData.at_risk_revenue += monthForecast.at_risk_revenue;
        monthData.one_time_expenses! += monthForecast.one_time_expenses || 0;
        monthData.recurring_expenses! += monthForecast.recurring_expenses || 0;
        monthData.total_expenses! += monthForecast.total_expenses || 0;
        monthData.net_profit! += monthForecast.net_profit || 0;
      }

      consolidated.push(monthData);
    }

    return consolidated;
  },

  // Scenario Management Methods
  async getScenarios(brandId: string): Promise<ForecastScenario[]> {
    const { data, error } = await supabase
      .from('forecast_scenarios')
      .select('*')
      .eq('brand_id', brandId)
      .order('is_default', { ascending: false })
      .order('scenario_name');

    if (error) throw error;
    return data || [];
  },

  async getDefaultScenario(brandId: string): Promise<ForecastScenario | null> {
    const { data, error } = await supabase
      .from('forecast_scenarios')
      .select('*')
      .eq('brand_id', brandId)
      .eq('is_default', true)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async createScenario(scenario: Omit<ForecastScenario, 'id' | 'created_at' | 'updated_at'>): Promise<ForecastScenario> {
    const { data, error } = await supabase
      .from('forecast_scenarios')
      .insert(scenario)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateScenario(scenarioId: string, updates: Partial<ForecastScenario>): Promise<ForecastScenario> {
    const { data, error } = await supabase
      .from('forecast_scenarios')
      .update(updates)
      .eq('id', scenarioId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteScenario(scenarioId: string): Promise<void> {
    const { error } = await supabase
      .from('forecast_scenarios')
      .delete()
      .eq('id', scenarioId);

    if (error) throw error;
  },

  // Churn Calculation Methods
  applyChurnToForecast(forecasts: MonthlyForecast[], churnRateMonthly: number): MonthlyForecast[] {
    if (churnRateMonthly === 0) {
      return forecasts.map(f => ({
        ...f,
        churn_adjusted_recurring_revenue: f.recurring_revenue,
        churn_adjusted_total_revenue: f.total_revenue,
        revenue_lost_to_churn: 0,
      }));
    }

    const adjustedForecasts: MonthlyForecast[] = [];
    let currentRecurringBase = 0;

    for (let i = 0; i < forecasts.length; i++) {
      const forecast = forecasts[i];

      if (i === 0) {
        currentRecurringBase = forecast.recurring_revenue;
      } else {
        currentRecurringBase = currentRecurringBase * (1 - churnRateMonthly);
      }

      const revenueLostToChurn = forecast.recurring_revenue - currentRecurringBase;
      const churnAdjustedRecurring = currentRecurringBase;
      const churnAdjustedTotal = churnAdjustedRecurring + forecast.prorated_revenue + forecast.project_revenue;

      adjustedForecasts.push({
        ...forecast,
        churn_adjusted_recurring_revenue: churnAdjustedRecurring,
        churn_adjusted_total_revenue: churnAdjustedTotal,
        revenue_lost_to_churn: revenueLostToChurn,
      });
    }

    return adjustedForecasts;
  },

  async generateForecastWithScenario(
    brandId: string,
    scenarioId: string | null,
    monthsAhead: number = 12
  ): Promise<MonthlyForecast[]> {
    const baseForecast = await this.generateForecast(brandId, monthsAhead);

    if (!scenarioId) {
      return baseForecast;
    }

    const { data: scenario, error } = await supabase
      .from('forecast_scenarios')
      .select('*')
      .eq('id', scenarioId)
      .maybeSingle();

    if (error || !scenario) {
      return baseForecast;
    }

    return this.applyChurnToForecast(baseForecast, scenario.churn_rate_monthly);
  },

  async generateProfitLossForecastWithScenario(
    brandId: string,
    scenarioId: string | null,
    monthsAhead: number = 12
  ): Promise<MonthlyForecast[]> {
    const forecastWithChurn = await this.generateForecastWithScenario(brandId, scenarioId, monthsAhead);
    const expenses = await this.getExpensesForBrand(brandId);

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    return forecastWithChurn.map((forecast, index) => {
      const forecastDate = new Date(currentYear, currentMonth + index, 1);
      const year = forecastDate.getFullYear();
      const month = forecastDate.getMonth();

      let oneTimeExpenses = 0;
      let recurringExpenses = 0;

      for (const expense of expenses) {
        if (expense.status === 'cancelled') continue;

        const expenseAmount = expense.allocated_amount || expense.amount;

        if (expense.expense_type === 'one_time' && expense.expense_date) {
          const expenseDate = new Date(expense.expense_date);
          if (expenseDate.getMonth() === month && expenseDate.getFullYear() === year) {
            oneTimeExpenses += expenseAmount;
          }
        } else if (expense.expense_type === 'recurring') {
          const startDate = expense.recurrence_start_date ? new Date(expense.recurrence_start_date) : null;
          const endDate = expense.recurrence_end_date ? new Date(expense.recurrence_end_date) : null;

          if ((!startDate || startDate <= forecastDate) && (!endDate || endDate >= forecastDate)) {
            recurringExpenses += expenseAmount;
          }
        }
      }

      const totalExpenses = oneTimeExpenses + recurringExpenses;
      const revenueForProfit = forecast.churn_adjusted_total_revenue || forecast.total_revenue;
      const netProfit = revenueForProfit - totalExpenses;

      return {
        ...forecast,
        one_time_expenses: oneTimeExpenses,
        recurring_expenses: recurringExpenses,
        total_expenses: totalExpenses,
        net_profit: netProfit,
      };
    });
  },

  async generateConsolidatedProfitLossWithScenario(
    brandIds: string[],
    scenarioIds: (string | null)[],
    monthsAhead: number = 12
  ): Promise<MonthlyForecast[]> {
    const forecasts = await Promise.all(
      brandIds.map((brandId, index) =>
        this.generateProfitLossForecastWithScenario(brandId, scenarioIds[index] || null, monthsAhead)
      )
    );

    const consolidated: MonthlyForecast[] = [];

    for (let i = 0; i < monthsAhead; i++) {
      let monthData: MonthlyForecast = {
        month: forecasts[0][i].month,
        recurring_revenue: 0,
        prorated_revenue: 0,
        project_revenue: 0,
        total_revenue: 0,
        confirmed_revenue: 0,
        at_risk_revenue: 0,
        one_time_expenses: 0,
        recurring_expenses: 0,
        total_expenses: 0,
        net_profit: 0,
        churn_adjusted_recurring_revenue: 0,
        churn_adjusted_total_revenue: 0,
        revenue_lost_to_churn: 0,
      };

      for (const forecast of forecasts) {
        const monthForecast = forecast[i];
        monthData.recurring_revenue += monthForecast.recurring_revenue;
        monthData.prorated_revenue += monthForecast.prorated_revenue;
        monthData.project_revenue += monthForecast.project_revenue;
        monthData.total_revenue += monthForecast.total_revenue;
        monthData.confirmed_revenue += monthForecast.confirmed_revenue;
        monthData.at_risk_revenue += monthForecast.at_risk_revenue;
        monthData.one_time_expenses! += monthForecast.one_time_expenses || 0;
        monthData.recurring_expenses! += monthForecast.recurring_expenses || 0;
        monthData.total_expenses! += monthForecast.total_expenses || 0;
        monthData.net_profit! += monthForecast.net_profit || 0;
        monthData.churn_adjusted_recurring_revenue! += monthForecast.churn_adjusted_recurring_revenue || monthForecast.recurring_revenue;
        monthData.churn_adjusted_total_revenue! += monthForecast.churn_adjusted_total_revenue || monthForecast.total_revenue;
        monthData.revenue_lost_to_churn! += monthForecast.revenue_lost_to_churn || 0;
      }

      consolidated.push(monthData);
    }

    return consolidated;
  },
};